import { useMemo } from 'react';

import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Avatar } from '@components/avatar';
import { Badge } from '@components/badge';
import { IconAssigned } from '@components/icons/assigned';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconMessages } from '@components/icons/messages';
import { IconPerson } from '@components/icons/person';
import { IconRatingMedium } from '@components/icons/rating-medium';
import { Stack } from '@components/stack';
import { NameCellLink, NotSetText } from '@components/table/renderers';
import { AvailableColumn } from '@components/table/types';
import { theme } from '@styles/theme';

import { Message } from 'api/messages';
import { applicationMessagesUrl, jobPipelineUrl } from 'features/routing';
import { useStore } from 'models/Provider';

export const useMessagesColumns = () => {
  const { t, userStore } = useStore();

  return useMemo<Array<AvailableColumn<Message>>>(
    () => [
      {
        id: 'created',
        title: t('messages.list.columns.date'),
        type: 'date',
      },
      {
        id: 'applicationName',
        title: t('messages.list.columns.applicationname'),
        type: 'text',
        renderHeaderIcon: () => <IconPerson size={16} />,
        Cell: ({ row }) => (
          <NameCellLink
            name={row.applicationName}
            avatar={<Avatar src={row.applicationPicture} name={row.applicationName} />}
            to={applicationMessagesUrl(row.applicationId, row.jobId)}
          />
        ),
      },
      {
        id: 'applicationEmail',
        title: t('messages.list.columns.applicationemail'),
        type: 'email',
        defaultHidden: true,
      },
      {
        id: 'message',
        title: t('messages.list.columns.message'),
        type: 'paragraph',
      },
      {
        id: 'jobTitle',
        title: t('messages.list.columns.jobtitle'),
        type: 'text',
        Cell: ({ row }) => (
          <ActionText.Link font="regular14" color="textPrimary" to={jobPipelineUrl(row.jobId)}>
            {row.jobTitle}
          </ActionText.Link>
        ),
      },
      {
        id: 'jobStageName',
        title: t('messages.list.columns.jobstagename'),
        type: 'text',
        defaultHidden: true,
      },
      {
        id: 'applicationStatus',
        title: t('messages.list.columns.applicationstatus'),
        type: 'text',
        sortable: false,
        defaultHidden: true,
        Cell: ({ row }) => (
          <ApplicationStatus $status={row.applicationStatus}>
            <IconRatingMedium /> {t('messages.list.status.' + row.applicationStatus)}
          </ApplicationStatus>
        ),
      },
      {
        id: 'applicationAssignedToId',
        title: t('messages.list.columns.assignedto'),
        type: 'user',
        sortable: false,
        renderHeaderIcon: () => <IconAssigned size={16} />,
        Cell: ({ row }) => {
          if (row.applicationAssignedToId) {
            const user = userStore.items.get(row.applicationAssignedToId);
            if (!user) {
              return <NotSetText />;
            }
            return <Avatar src={user.picture} name={user.name} />;
          }
          return null;
        },
      },
      {
        id: 'applicationNumMessages',
        title: t('messages.list.columns.nummessages'),
        type: 'number',
        renderHeaderIcon: () => <IconMessages size={16} />,
        Cell: ({ row }) => (
          <Stack orientation="horizontal" space={1} align="center">
            {row.failed && <IconFormIndicatorError size={24} />}
            <Badge
              variant={row.unread ? 'green' : 'neutral'}
              size="md"
              text={row.applicationNumMessages}
            />
          </Stack>
        ),
      },
    ],
    [t, userStore],
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'hired':
      return theme.color.elementGreen200;
    case 'rejected':
      return theme.color.red100;
    case 'retracted':
      return theme.color.orange100;
    default:
      return theme.color.elementBlue100;
  }
};

const ApplicationStatus = styled.span<{ $status: string }>((p) => ({
  display: 'flex',
  alignItems: 'center',
  font: theme.font.regular14,
  color: getStatusColor(p.$status),
  textTransform: 'capitalize',
}));
